import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import {
    useCancelReservationsByCreditHoldMutation,
    useGetAllReservationsQuery,
    useGetIndividualCreditHoldQuery,
    useRemoveCreditHoldMutation,
} from '../../../app/Slices/SevenDayApiSlice';
import React from 'react';
import { Close, CreditCardOff } from '@mui/icons-material';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import toast from '../../shared/Toast';
import { ModalProps } from '../../types/modal';
import { MRT_ColumnDef } from 'mantine-react-table';
import GenericTable from '../../shared/GenericTable';
import { CustomChip } from '../../../layout/styledcomponents/CustomChip';
import { capitalizeName } from '../../../shared/utils';

interface Props extends ModalProps {
    id: number;
}
const CreditHoldModal = ({ id, open, onClose }: Props) => {
    const isMobile = useMediaQuery('(max-width: 770px)');

    const { data: creditHold } = useGetIndividualCreditHoldQuery(
        { id },
        { skip: !id },
    );
    const [removeCreditHold, { isLoading: isRemoving }] =
        useRemoveCreditHoldMutation();
    const [cancelReservations, { isLoading: isCancelling }] =
        useCancelReservationsByCreditHoldMutation();

    const handleRemoveCreditHold = async () => {
        try {
            await removeCreditHold({ id }).unwrap();
            toast.success('Credit hold removed successfully');
        } catch (error) {
            toast.error('Error removing credit hold');
        }
    };

    const handleCancelReservations = async () => {
        try {
            await cancelReservations({ id }).unwrap();
            toast.success('Upcoming reservations cancelled successfully');
        } catch (error) {
            toast.error('Error cancelling reservations');
        }
    };

    const { data: cancelledReservations } = useGetAllReservationsQuery(
        { creditHold: creditHold?.id },
        { skip: !creditHold },
    );

    const { data: upcomingReservations } = useGetAllReservationsQuery(
        {
            guest: creditHold?.guest.id,
            status: ['approved', 'pending'],
            startDate: 'future-excluding-today',
        },
        { skip: !creditHold },
    );

    const handleClose = () => onClose();

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'id',
            header: 'Reservation Id',
            Cell: ({ cell }) => {
                return `#${cell.getValue()}`;
            },
        },
        {
            accessorKey: 'module.name',
            header: 'Experience',
        },
        {
            accessorKey: 'startDate',
            header: 'Check-in',
            Cell: ({ cell }) => {
                return new Date(cell.getValue<string>()).toLocaleDateString(
                    'en-AU',
                );
            },
        },
        {
            accessorKey: 'endDate',
            header: 'Checkout',
            Cell: ({ cell }) => {
                return new Date(cell.getValue<string>()).toLocaleDateString(
                    'en-AU',
                );
            },
        },
        {
            accessorKey: 'createdAt',
            header: 'Booked',
            Cell: ({ cell }) => {
                return new Date(cell.getValue<string>()).toLocaleDateString(
                    'en-AU',
                );
            },
        },
        {
            accessorKey: 'status',
            header: 'Status',
            Cell: ({ cell }) => {
                const value = cell.getValue<string>();
                return (
                    <CustomChip
                        variant='outlined'
                        label={capitalizeName(value)}
                        color={value === 'approved' ? 'yellow' : 'blue'}
                    />
                );
            },
        },
    ];

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='xl'>
            <DialogTitle>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        minWidth: 0,
                        color: 'red',
                    }}
                >
                    Credit Hold # {id}
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    {creditHold && !creditHold.removedAt && (
                        <CustomButton
                            variant='outlined'
                            color='warning'
                            type='submit'
                            disabled={isRemoving}
                            onClick={handleRemoveCreditHold}
                        >
                            Release Credit Hold
                        </CustomButton>
                    )}
                    <IconButton
                        onClick={handleClose}
                        sx={{ color: 'text.primary' }}
                        aria-label='close'
                    >
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent
                sx={{
                    width: '100%',
                    minWidth: { xs: '100%', md: '800px' },
                    px: { xs: 1, sm: 3 },
                }}
            >
                {creditHold && (
                    <Stack spacing={1}>
                        <Typography>
                            <span style={{ fontWeight: 'bold' }}>
                                {' '}
                                Guest Company:{' '}
                            </span>{' '}
                            {creditHold.guest.name}
                        </Typography>

                        <Typography>
                            <span style={{ fontWeight: 'bold' }}>
                                Placed at:{' '}
                            </span>
                            {new Date(creditHold.placedAt).toLocaleDateString(
                                'en-AU',
                            )}
                        </Typography>
                        <Typography>
                            <span style={{ fontWeight: 'bold' }}>
                                Removed at:{' '}
                            </span>
                            {creditHold.removedAt
                                ? new Date(
                                      creditHold.removedAt,
                                  ).toLocaleDateString('en-AU')
                                : 'N/A'}
                        </Typography>
                        <Typography sx={{ pt: 2, fontWeight: 'bold' }}>
                            Cancelled Reservations
                        </Typography>
                        {cancelledReservations &&
                        !cancelledReservations.length ? (
                            <Typography sx={{ fontSize: 12 }}>
                                No reservations were cancelled by this credit
                                hold.
                            </Typography>
                        ) : (
                            <>
                                <Typography sx={{ fontSize: 12 }}>
                                    The reservations below were cancelled by
                                    this credit hold.
                                </Typography>
                                <GenericTable
                                    columns={columns}
                                    data={cancelledReservations ?? []}
                                    tableName='Cancelled-Reservations'
                                    loading={!cancelledReservations}
                                />
                            </>
                        )}
                        <Typography sx={{ pt: 2, fontWeight: 'bold' }}>
                            Upcoming Reservations
                        </Typography>
                        {upcomingReservations &&
                        !upcomingReservations.length ? (
                            <Typography sx={{ fontSize: 12 }}>
                                The guest company has no upcoming reservation.
                            </Typography>
                        ) : (
                            <>
                                <Stack
                                    spacing={1}
                                    sx={{ minWidth: 0 }}
                                    direction={isMobile ? 'column' : 'row'}
                                    justifyContent={'space-between'}
                                >
                                    <Typography sx={{ fontSize: 12 }}>
                                        The guest company has below upcoming
                                        reservations.
                                    </Typography>
                                    {!creditHold.removedAt && (
                                        <CustomButton
                                            variant='contained'
                                            color='warning'
                                            type='submit'
                                            disabled={isCancelling}
                                            onClick={handleCancelReservations}
                                        >
                                            Cancel Upcoming Reservations
                                        </CustomButton>
                                    )}
                                </Stack>
                                <GenericTable
                                    columns={columns}
                                    data={upcomingReservations ?? []}
                                    tableName='Cancelled-Reservations'
                                    loading={!upcomingReservations}
                                />
                            </>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant='contained' color='secondary' onClick={onClose}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreditHoldModal;
